import React from "react";
import { useEffect, useState } from "react";
import { initialPaginationObj } from "../../../episode/hooks/useEpisodeHook";
import { useOutletContext } from "react-router";
import { message, PaginationProps, Modal, Form, Tag } from "antd";
import axios from "axios";
import { getSegmentsData, updateSegmentData, createSegmentData, deleteSegmentData, getPlatformCaptionLanguages, PlatformCaptionLanguagesData } from "../../../utils";
import { authAxios } from "../../../utils/session_utils";

export interface GetSegmentsDataHandlerParams {
	currentPaginationInfo?: PaginationProps;
	newFilterObj?: Record<string, unknown> | null;
	searchString?: string;
}

export const useListingHook = () => {
	const [form] = Form.useForm();
    const [addSegmentForm] = Form.useForm();
	const [ filterForm ] = Form.useForm();
    const [ bulkSegmentationForm ] = Form.useForm();

	const { user, distributionPlatformData }: any = useOutletContext();
	const [listingData, setListingData] = useState([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [pagination, setPagination] = useState<PaginationProps>({
		...initialPaginationObj,
		pageSize: user.page_sizes?.metadata ?? 10,
	});

	const [editRecord, setEditRecord] = useState<any>(null);
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isAddSegmentModalOpen, setIsAddSegmentModalOpen] = useState(false);
	const [isFilteredData, setIsFilteredData] = useState(false);
	const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [isBulkSegmentationModalOpen, setIsBulkSegmentationModalOpen] = useState<boolean>(false);
    const [captionOrder, setCaptionOrder] = useState<string[]>([]);
    const [captionInfo, setCaptionInfo] = useState<PlatformCaptionLanguagesData | null>(null);
    const [isCaptionInfoLoading, setIsCaptionInfoLoading] = useState<boolean>(false);
	const [filterObj, setFilterObj] = useState<any>({});

	useEffect(() => {
		setIsLoading(true);
		getDataHandler({ currentPaginationInfo: pagination });
	}, [pagination.pageSize, pagination.current]);

	useEffect(() => {
		console.log("Edit Record", editRecord);
		if (editRecord) {
			form.setFieldsValue(editRecord);
			setIsEditModalOpen(true);
		} else {
			form.resetFields();
		}
	}, [editRecord]);

	const handleTableChange = (
		pagination: PaginationProps,
		filters: any,
		sorter: any,
		{ action }: any
	) => {
		if (action === "paginate") {
			setIsLoading(true);
			getDataHandler({
				currentPaginationInfo: pagination,
			});
		}
	};

    const handleAddSegmentSubmit = async (values: any) => {
        setIsLoading(true);

        try {
            let response;

            if (values.file) {
                const file: File = values.file;
                const contentType = file.type || "application/octet-stream";
                const presignRes = await authAxios({
                    method: "GET",
                    url: `${process.env.REACT_APP_BACK_END_API}/snowflake/segments/upload-url`,
                    params: { fileName: file.name, contentType },
                });

                if (presignRes.data.error || !presignRes.data.url) {
                    message.error(presignRes.data.error || "Could not get upload URL");
                    setIsLoading(false);
                    return;
                }

                const { url, publicUrl } = presignRes.data;
                await axios.put(url, file, {
                    headers: { "Content-Type": contentType },
                });

                response = await authAxios({
                    method: "POST",
                    url: `${process.env.REACT_APP_BACK_END_API}/snowflake/segments`,
                    data: {
                        title: values.title,
                        assetIdentifier: values.assetIdentifier,
                        mediaType: values.mediaType,
                        uploadedS3Url: publicUrl,
                    },
                });
            } else {
                response = await authAxios({
                    method: "POST",
                    url: `${process.env.REACT_APP_BACK_END_API}/snowflake/segments`,
                    data: {
                        title: values.title,
                        assetIdentifier: values.assetIdentifier,
                        mediaType: values.mediaType,
                        videoUrl: values.videoUrl,
                    },
                });
            }

            if (!response.data.error) {
                message.success(response.data.message || "Segment upload job submitted");
                setIsAddSegmentModalOpen(false);
                addSegmentForm.resetFields();
            } else {
                message.error(response.data.message || "Error submitting segment upload");
            }
        } catch (err) {
            console.error("Add Segment Error", err);
            message.error("Error submitting segment upload");
        } finally {
            setIsLoading(false);
        }
    };

	const handleEditSubmit = async (values: any) => {
		console.log("Edit Submit", values);
		setIsLoading(true);
		console.log("Edit Record", values.id);
		if(values.id && values.id.length > 0) {
			await updateSegmentData(values, (res: any) => {
				message.success("Segment updated successfully");
				setEditRecord(null);
				setIsEditModalOpen(false);
				getDataHandler({
					currentPaginationInfo: pagination,
				});
			},
			(err: any) => {
				console.error("Update Error", err);
				message.error("Error updating segment");
				setIsLoading(false);
			});
		} else {
			await createSegmentData(values, (res: any) => {
				message.success("Segment created successfully");
				setEditRecord(null);
				setIsEditModalOpen(false);	
				getDataHandler({
					currentPaginationInfo: pagination,
				});
			},
			(err: any) => {
				console.error("Create Error", err);
				message.error("Error creating segment");
				setIsLoading(false);
			});
		}
	};

	const handleEdit = (record: any) => {
		console.log("Edit", record);
		setEditRecord(record);
	};

	const handleDelete = async (record: any) => {
		Modal.confirm({
			title: "Are you sure you want to delete this segment?",
			onOk: async () => {
				console.log("Delete", record);
				await deleteSegmentData(record.id, (res: any) => {
					console.log("Delete Success", res);
					message.success("Segment deleted successfully");
					getDataHandler({
						currentPaginationInfo: pagination,
					});
				},
				(err: any) => {
					console.error("Delete Error", err);
					message.error("Error deleting segment");
					setIsLoading(false);
				});
			},
		});
	};

	const getDataHandler = async ({
		newFilterObj = {},
		searchString,
		currentPaginationInfo,
	}: GetSegmentsDataHandlerParams) => {
		setIsLoading(true);
		let reqFilterObj = { ...filterObj, ...newFilterObj };
		if(newFilterObj === null) {
			reqFilterObj = { };
		}

		await getSegmentsData(
			setListingData,
			setIsLoading,
			(currentPaginationInfo as PaginationProps) || pagination,
			setPagination,
			reqFilterObj,
		);
	};

    const getListingColumns = () => {
        const columnsListing = [
            {
                title: "Parent Feed",
                dataIndex: "parent_feed",
                key: "parent_feed",
                width: "4%",
            },
            {
                title: "Feeds",
                dataIndex: "feed_data",
                key: "feed_data",
                width: "10%",
                render: (text: any, record: any) => {
                    // Handle new format with feed_data or fallback to old feed_names format
                    let feeds: any[] = [];
                   
                    if (record.feed_data) {
                        // Parse feed data: format is "feed_id|feed_name|is_parent||feed_id|feed_name|is_parent"
                        const feedEntries = record.feed_data.split('||').filter((entry: string) => entry.trim());
                        feeds = feedEntries.map((entry: string) => {
                            const [feedId, feedName, isParent] = entry.split('|');
                            return {
                                id: feedId,
                                name: feedName,
                                isParent: isParent === '1'
                            };
                        });
                    } else if (record.feed_names) {
                        // Fallback to old format (just names, no IDs or parent info)
                        const feedNames = record.feed_names.split(', ').filter((name: string) => name.trim());
                        feeds = feedNames.map((name: string) => ({
                            id: 'N/A',
                            name: name.trim(),
                            isParent: false
                        }));
                    }
                   
                    if (feeds.length === 0) {
                        return <span>—</span>;
                    }
 
                    const colorMap: Record<string, string> = {
                        "2": "cyan",
                        "3": "purple",
                        "4": "orange",
                        "5": "green",
                    };
 
                    return (
                        <span>
                            {feeds.map((feed: any, index: number) => (
                                <Tag
                                    key={index}
                                    color={feed.isParent ? "red" : colorMap[feed.id] || "blue"}
                                    style={{
										marginBottom: '4px',
										fontWeight: feed.isParent ? 'bold' : 'normal',
										border: feed.isParent ? '2px solid #ff4d4f' : undefined
                                    }}
                                >
                                    {feed.name} ({feed.id})
                                </Tag>
                            ))}
                        </span>
                    );
                },
            },
            {
                title: "Type",
                dataIndex: "segment_type",
                key: "segment_type",
                width: "5%",
            },
            {
                title: "title",
                dataIndex: "title",
                key: "title",
                width: "10%",
            },
            {
                title: "Duration",
                dataIndex: "duration",
                key: "duration",
                width: "5%",
            },
            {
                title: "Seg #",
                dataIndex: "segment_num",
                key: "segment_num",
                width: "4%",
            },
            {
                title: "Asset ID",
                dataIndex: "asset_identifier",
                key: "asset_identifier",
                width: "15%",
            },
            {
                title: "Media Type",
                dataIndex: "media_type",
                key: "media_type",
                width: "5%",
            },
            {
                title: "Series Code",
                dataIndex: "series_code",
                key: "series_code",
                width: "5%",
            },
            {
                title: "Ref ID",
                dataIndex: "ref_id",
                key: "ref_id",
                width: "5%",
            },
            {
                title: "SeriesID",
                dataIndex: "series_id",
                key: "series_id",
                width: "6%",
                render: (text: any, record: any) => {
                    return (
                        <span>
                            {record.series_id ? (
                                record.series_id.length > 10 ? (
                                    <span title={record.series_id}>{record.series_id.substring(0, 10)}...</span>
                                ) : (
                                    record.series_id
                                )
                            ) : (
                                "NULL"
                            )}
                        </span>
                    );
                },
            },
            {
                title: "EpisodeID",
                dataIndex: "episode_id",
                key: "episode_id",
                width: "6%",
                render: (text: any, record: any) => {
                    return (
                        <span>
                            {record.episode_id ? (
                                record.episode_id.length > 10 ? (
                                    <span title={record.episode_id}>{record.episode_id.substring(0, 10)}...</span>
                                ) : (
                                    record.episode_id
                                )
                            ) : (
                                "NULL"
                            )}
                        </span>
                    );
                },
            },
            {
                title: "S3 Url",
                dataIndex: "s3_url", 
                key: "s3_url",
                width: "15%",
                render: (text: string) => {
                    return text ? text : "NULL"
                }
            },
            {
                title: "Edit/Delete",
                width: "10%",
                render: (text: any, record: any) => {
                    return (
                        <span>
                            <a
                                href="#"
                                onClick={() => {
                                    handleEdit(record);
                                    console.log("Edit", record);
                                }}
                            >
                                Edit
                            </a>
                            &nbsp;|&nbsp;
                            <a
                                href="#"
                                onClick={() => {
                                    console.log("Delete", record);
                                    handleDelete(record);
                                }}
                            >
                                Delete
                            </a>
                        </span>
                    );
                },
                dataIndex: "actions",
            },
        ];
        return columnsListing;
    };

	const clearFilter = async () => {
		// setLoading(true);
		setIsLoading(true);
		setIsFilteredData(false);
		const newFilterObj = { };
		filterForm.resetFields();
		setFilterObj(newFilterObj);
		pagination.current = 1;
		setPagination({
			...pagination,
			current: 1,
		});
		await getDataHandler({
			newFilterObj: null,
			currentPaginationInfo: pagination,
		});
	};

	const handleFilterSubmit = async () => {
		setIsLoading(true);
		const newFilterObj = {
			seriesId: filterForm.getFieldValue("seriesId"),
			segmentFeed: filterForm.getFieldValue("segmentFeed"),
			segmentType: filterForm.getFieldValue("segmentType"),
			searchString: filterForm.getFieldValue("searchString"),
		};
		console.log("Filter Object", newFilterObj);
		setFilterObj(newFilterObj);
		setIsFilteredData(true);
		setIsFilterModalOpen(false);
		pagination.current = 1;
		setPagination({
			...pagination,
			current: 1,
		});
		await getDataHandler({
			newFilterObj,
			currentPaginationInfo: pagination,
		});
	}

    const handleCaptionPlatformChange = async (platformId?: string) => {
        setCaptionInfo(null);
        setCaptionOrder([]);
        if (!platformId) return;
        try {
            setIsCaptionInfoLoading(true);
            const data = await getPlatformCaptionLanguages(platformId);
            setCaptionInfo(data);
            setCaptionOrder(data.orderedLanguages || []);
        } catch (error) {
            console.error({ error });
            message.error("Failed to load caption languages for this platform");
        } finally {
            setIsCaptionInfoLoading(false);
        }
    };

    const moveCaptionLanguage = (index: number, direction: -1 | 1) => {
        setCaptionOrder((prev) => {
            const target = index + direction;
            if (target < 0 || target >= prev.length) return prev;
            const next = [...prev];
            [next[index], next[target]] = [next[target], next[index]];
            return next;
        });
    };

    const handleBulkSegmentation = async () => {
        try {
            setIsBulkSegmentationModalOpen(false);
            const values = await bulkSegmentationForm.validateFields();
            const data = {
                distributionPlatformId: values.distributionPlatform,
                redoExistingSegments: values.redoExistingSegments ?? false,
                notifyEmail: user.email,
                captionLanguagePriority: captionOrder,
            }
            setIsLoading(true);
            const config: any = {
                method: "POST",
                url: `${process.env.REACT_APP_BACK_END_API}/snowflake/generate-segments`,
                data: data
            };

            const response = await authAxios(config);

            if (!response.data.error) {
                message.success(response.data.message);
                bulkSegmentationForm.resetFields();
                setCaptionInfo(null);
                setCaptionOrder([]);
            } else {
               message.error(response.data.message);
            }
            setIsLoading(false);
        } catch (error) {
            console.error({ error });
        } finally {
            setIsLoading(false);
        }
    };

	return {
		getListingColumns,
		listingData,
		isLoading,
		pagination,
		handleTableChange,
		isEditModalOpen,
		setIsEditModalOpen,
        isAddSegmentModalOpen,
        setIsAddSegmentModalOpen,
		editRecord,
		setEditRecord,
		handleEditSubmit,
		form,
        addSegmentForm,
		isFilteredData,
		setIsFilterModalOpen,
		isFilterModalOpen,
		clearFilter,
		filterForm,
        distributionPlatformData,
		handleFilterSubmit,
        bulkSegmentationForm,
        isBulkSegmentationModalOpen,
        setIsBulkSegmentationModalOpen,
        handleBulkSegmentation,
        handleAddSegmentSubmit,
        captionOrder,
        captionInfo,
        isCaptionInfoLoading,
        handleCaptionPlatformChange,
        moveCaptionLanguage
	};
};
